/**
 * LES TROIS ISSUES D'UN PAIEMENT, ET POURQUOI IL Y EN A TROIS.
 *
 * « Réussi » et « échoué » ne suffisent pas. Le compte accepte des moyens à redirection — Klarna,
 * Bancontact, EPS — et `allow_redirects: 'always'` est désormais écrit dans `coaching-payment`.
 * Avec eux, le navigateur revient AVANT que la banque ait tranché : le paiement est alors
 * `processing`, ni encaissé ni refusé. Le ranger du côté de l'échec dirait à quelqu'un qui a
 * réellement payé que son paiement a échoué — et l'inviterait à recommencer, donc à payer deux
 * fois. Le ranger du côté du succès promettrait un créneau qui peut encore être refusé.
 *
 * CE MODULE N'EST PAS DANS LE SHIM, ET C'EST VOULU. Il ne dépend d'aucune plateforme : le
 * vocabulaire doit être le MÊME des deux côtés, sans quoi la route de retour et la feuille native
 * parleraient deux langues pour décrire la même chose.
 */

/** Issue d'un paiement, du point de vue de ce qu'on peut en dire à l'utilisateur. */
export type EtatPaiement = 'succeeded' | 'processing' | 'failed' | 'canceled';

/**
 * Deux vocabulaires Stripe entrent ici, et c'est délibéré.
 *
 *  • `status` d'un PaymentIntent : `succeeded`, `processing`, `requires_action`, `canceled`…
 *  • `redirect_status` ajouté à la `return_url` : `succeeded`, `pending`, `failed`.
 *
 * Le lot (a) ne lit que le second, faute de `stripe-js`. Le lot (b) lira le premier, qui fait
 * autorité. Les deux passent par cette fonction pour que ce remplacement ne change AUCUN des
 * libellés affichés — seulement la fiabilité de l'entrée.
 */
const TABLE: Record<string, EtatPaiement> = {
  // Encaissé, des deux vocabulaires.
  succeeded: 'succeeded',

  // EN COURS — la banque n'a pas tranché. `pending` vient du retour de redirection,
  // `processing` du PaymentIntent.
  pending: 'processing',
  processing: 'processing',
  // La personne doit encore faire quelque chose (authentification bancaire) : ce n'est pas un
  // refus. Classé en cours pour ne jamais annoncer un échec qui n'a pas eu lieu.
  requires_action: 'processing',
  requires_confirmation: 'processing',
  // Autorisé mais non capturé. Ne devrait pas survenir en capture automatique ; s'il survient,
  // l'argent n'est pas encore pris — donc pas un succès.
  requires_capture: 'processing',

  // Refusé, ou moyen de paiement à redonner.
  failed: 'failed',
  requires_payment_method: 'failed',

  canceled: 'canceled',
};

/**
 * Traduit un statut Stripe en issue affichable.
 *
 * UN STATUT INCONNU DEVIENT `processing`, JAMAIS `failed`. Stripe ajoute des statuts au fil des
 * versions, et se tromper n'a pas le même coût des deux côtés : annoncer un échec sur un paiement
 * qui aboutira pousse à repayer, alors qu'annoncer « en cours » sur un paiement échoué se corrige
 * au rafraîchissement suivant. Entre deux erreurs, on choisit celle qui ne coûte pas d'argent.
 */
export function interpreterStatut(valeur: string | null | undefined): EtatPaiement {
  if (!valeur) return 'processing';
  return TABLE[valeur] ?? 'processing';
}

/** Un paiement en cours n'est ni un succès ni un échec : aucun appelant ne doit avoir à le déduire. */
export function estDefinitif(etat: EtatPaiement): boolean {
  return etat === 'succeeded' || etat === 'failed' || etat === 'canceled';
}

/**
 * Le SDK natif (`@stripe/stripe-react-native`) nomme les statuts en PascalCase — `RequiresAction`,
 * `Succeeded` — là où l'API Stripe écrit `requires_action`. On normalise avant `interpreterStatut`,
 * faute de quoi tout statut natif retomberait sur « en cours ».
 */
export function normaliserStatutNatif(statut: string | null | undefined): string {
  if (!statut) return '';
  return statut.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
}
